import { z } from "zod";

// ── Bank account type labels ──────────────────────────────────────────────────
// Display mapping for the provider-reported account type. The canonical value
// lives on `bank_connection_account.subtype` (checking | savings | …) with
// `type` (depository | credit | …) as a fallback — it is NOT stored on the
// bank_account table.

export const BANK_ACCOUNT_TYPE_LABELS: Record<string, string> = {
  checking: "Checking",
  savings: "Savings",
  credit_card: "Credit Card",
  cash: "Cash",
  investment: "Investment",
  loan: "Loan",
  other: "Other",
};

export function bankAccountTypeLabel(type: string | null | undefined): string {
  if (!type) return "—";
  return BANK_ACCOUNT_TYPE_LABELS[type] ?? type;
}

// ── Domain schema (DB / server truth) ─────────────────────────────────────────
// Mirrors the `bank_account` table. `accountCode` / `accountName` /
// `accountType` are joined from the linked `chart_account` row for display;
// `accountId` (NOT NULL) references `chart_account.id`.

export const BankAccountSchema = z.object({
  id: z.string(),
  businessId: z.string(),
  name: z.string(),
  description: z.string().optional(),
  accountId: z.string(),
  accountCode: z.string().optional(),
  accountName: z.string().optional(),
  accountType: z.string().optional(),
  // Connection details — null/undefined for manually-created accounts; set
  // when the account is linked to a provider connection.
  bankConnectionId: z.string().optional(),
  provider: z.string().optional(),
  providerAccountId: z.string().optional(),
  createdAt: z.union([z.string(), z.date()]),
  updatedAt: z.union([z.string(), z.date()]),
});

export type BankAccount = z.infer<typeof BankAccountSchema>;

export const CreateBankAccountSchema = BankAccountSchema.omit({
  id: true,
  businessId: true,
  accountCode: true,
  accountName: true,
  accountType: true,
  createdAt: true,
  updatedAt: true,
});

export const UpdateBankAccountSchema = CreateBankAccountSchema.partial();

export type CreateBankAccountInput = z.infer<typeof CreateBankAccountSchema>;
export type UpdateBankAccountInput = z.infer<typeof UpdateBankAccountSchema>;

// ── Form schema (UI layer) ────────────────────────────────────────────────────

export const BankAccountFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  description: z.string().optional(),
  accountId: z.string().min(1, "Linked chart account is required"),
});

export type BankAccountForm = z.infer<typeof BankAccountFormSchema>;

// ── Mapping: Form → Domain ────────────────────────────────────────────────────

export function formToDomain(input: BankAccountForm): UpdateBankAccountInput {
  return {
    name: input.name,
    description: input.description || undefined,
    accountId: input.accountId,
  };
}

export function domainToForm(item: BankAccount): BankAccountForm {
  return {
    name: item.name,
    description: item.description ?? "",
    accountId: item.accountId,
  };
}
